import { useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, createFileRoute, redirect, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { usePageBar } from "../../components/shell/page-bar";
import { merchSummaryOptions } from "../../lib/api/merch";
import { canEditMerchStaff, canSeeMerch } from "../../lib/merch/access";

/**
 * Merchandising — the layout: a tab row over the page.
 *   Orders (badge: paid orders to pack) · Products · Promotions · Customers ·
 *   Outbox · Staff (SUPER_ADMIN only)
 *
 * Who: `/auth/me.merch_access` (SUPER_ADMIN, or an ADMIN on the merch staff
 * list — the API's own SQL rule). A courtesy, not the control: every
 * `/merch` route is fenced by the API.
 */
export const Route = createFileRoute("/_app/merchandising")({
	beforeLoad: ({ context }) => {
		if (!canSeeMerch(context.session)) throw redirect({ to: "/" });
	},
	staticData: { contentTop: "tabbed" },
	component: MerchLayout,
});

type TabTo =
	| "/merchandising/orders"
	| "/merchandising/products"
	| "/merchandising/promos"
	| "/merchandising/customers"
	| "/merchandising/outbox"
	| "/merchandising/staff";
type TabKey = "orders" | "products" | "promos" | "customers" | "outbox" | "staff";

function MerchLayout() {
	const { t } = useTranslation("merch");
	const { t: tn } = useTranslation("nav");
	const { formatNumber: fmt } = useUiStrings();
	const { session } = Route.useRouteContext();
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const summary = useQuery(merchSummaryOptions());
	const toPack = summary.data?.by_status.find((s) => s.status === "PAID")?.count;

	usePageBar({ title: tn("items.merchandising"), subtitle: tn("sections.operations") });

	const tabs: { key: TabKey; to: TabTo; badge?: number; badgeHint?: string }[] = [
		{ key: "orders", to: "/merchandising/orders", badge: toPack, badgeHint: toPack !== undefined ? t("tabs.toPack", { count: toPack, n: fmt(toPack) }) : undefined },
		{ key: "products", to: "/merchandising/products" },
		{ key: "promos", to: "/merchandising/promos" },
		{ key: "customers", to: "/merchandising/customers" },
		{ key: "outbox", to: "/merchandising/outbox" },
	];
	if (canEditMerchStaff(session)) tabs.push({ key: "staff", to: "/merchandising/staff" });
	const current = (to: TabTo) => pathname === to || pathname.startsWith(`${to}/`);

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<nav aria-label={t("tabs.label")} className="-mt-[0.25rem] flex gap-[0.25rem] overflow-x-auto border-b border-gms-line [scrollbar-width:none]">
				{tabs.map((tab) => (
					<Link
						key={tab.key}
						to={tab.to}
						aria-current={current(tab.to) ? "page" : undefined}
						className={`relative inline-flex h-[2.75rem] shrink-0 items-center gap-[0.375rem] px-[0.75rem] text-[0.875rem] whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-gms-navy/40 ${
							current(tab.to)
								? "font-semibold text-gms-navy after:absolute after:inset-x-[0.5rem] after:-bottom-px after:h-[3px] after:rounded-t-[3px] after:bg-gms-red"
								: "font-medium text-gms-ink-2 hover:text-gms-navy"
						}`}
					>
						{t(`tabs.${tab.key}`)}
						{!!tab.badge && tab.badge > 0 && (
							<span
								title={tab.badgeHint}
								className="inline-grid h-[1.125rem] min-w-[1.25rem] place-items-center rounded-full bg-gms-red px-[0.3125rem] text-[0.6875rem] font-bold text-white tabular-nums"
							>
								{fmt(tab.badge)}
							</span>
						)}
					</Link>
				))}
			</nav>
			<Outlet />
		</div>
	);
}
